import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AffaireService } from '../../../core/services/affaire.service';
import { ClientService } from '../../../core/services/client.service';
import { Affaire, Client, PageResponse } from '../../../core/models/models';
import { AffaireFilesDialogComponent } from './affaire-files-dialog.component';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog.component';

@Component({
  selector: 'app-affaire-edit',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, MatIconModule, AffaireFilesDialogComponent, ConfirmDialogComponent],
  templateUrl: './affaire-edit.component.html',
  styleUrls: ['./affaire-edit.component.css']
})
export class AffaireEditComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private affaireService = inject(AffaireService);
  private clientService = inject(ClientService);

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  affaireId!: number;
  affaire = signal<Affaire | null>(null);
  showDeleteModal = signal(false);
  canDelete = signal(false);
  isArchived = signal(false);

  // Files dialog state
  showFilesDialog = signal(false);

  // Client dropdown state
  clientOptions = signal<Client[]>([]);
  clientSearch = signal('');
  clientPage = signal(0);
  clientTotalPages = signal(0);
  showClientDropdown = signal(false);
  isLoadingClients = signal(false);

  affaireForm = this.fb.group({
    numeroAffaire: ['', Validators.required],
    client: [null as Client | null, Validators.required]
  });


  ngOnInit() {
    this.affaireId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadAffaire();
    this.loadClients();
  }

  loadAffaire() {
    this.affaireService.getAffaireById(this.affaireId).subscribe({
      next: (a: Affaire) => {
        this.affaire.set(a);
        this.isArchived.set(a.archived || false);
        this.affaireForm.patchValue({
          numeroAffaire: a.numeroAffaire,
          client: a.client
        });
        this.checkDeleteStatus();
      }
    });
  }

  checkDeleteStatus() {
    if (!this.affaireId) return;
    this.affaireService.canDeleteAffaire(this.affaireId).subscribe({
      next: (res) => this.canDelete.set(res),
      error: () => this.canDelete.set(false)
    });
  }

  onDeleteClick() {
    this.showDeleteModal.set(true);
  }

  confirmDelete() {
    if (this.affaireId) {
      this.isLoading.set(true);
      this.affaireService.deleteAffaire(this.affaireId).subscribe({
        next: () => {
          this.router.navigate(['/affaires']);
        },
        error: () => {
          this.errorMessage.set('Erreur lors de la suppression/archivage de l\'affaire.');
          this.isLoading.set(false);
          this.showDeleteModal.set(false);
        }
      });
    }
  }

  cancelDelete() {
    this.showDeleteModal.set(false);
  }

  openFilesDialog() {
    this.showFilesDialog.set(true);
  }

  closeFilesDialog() {
    this.showFilesDialog.set(false);
  }

  toggleClientDropdown() {
    this.showClientDropdown.set(!this.showClientDropdown());
    if (this.showClientDropdown()) {
      this.resetAndLoadClients();
    }
  }

  onClientSearchChange(value: string) {
    this.clientSearch.set(value);
    this.resetAndLoadClients();
  }

  resetAndLoadClients() {
    this.clientOptions.set([]);
    this.clientPage.set(0);
    this.clientTotalPages.set(0);
    this.loadClients();
  }

  loadClients() {
    if (this.isLoadingClients()) return;
    this.isLoadingClients.set(true);
    this.clientService.getClients(this.clientSearch(), this.clientPage(), 10, 'denomination,asc')
      .subscribe({
        next: (resp: PageResponse<Client>) => {
          this.clientOptions.set([...(this.clientOptions()), ...resp.content]);
          this.clientTotalPages.set(resp.totalPages);
          this.isLoadingClients.set(false);
        },
        error: () => {
          this.isLoadingClients.set(false);
        }
      });
  }

  loadMoreClients() {
    if (this.clientPage() + 1 >= this.clientTotalPages()) return;
    this.clientPage.set(this.clientPage() + 1);
    this.loadClients();
  }

  selectClient(client: Client) {
    this.affaireForm.patchValue({ client });
    this.showClientDropdown.set(false);
  }

  displaySelectedClient(): string {
    const c = this.affaireForm.value.client as Client | null;
    return c ? `${c.denomination} (${c.numeroClient})` : 'Sélectionner un client';
  }

  onSubmit() {
    if (this.affaireForm.invalid) return;
    this.isLoading.set(true);
    this.errorMessage.set(null);

    const value = this.affaireForm.getRawValue();
    const payload: Partial<Affaire> = {
      numeroAffaire: value.numeroAffaire!,
      client: { id: (value.client as Client).id } as Client
    };

    this.affaireService.updateAffaire(this.affaireId, payload as Affaire).subscribe({
      next: () => {
        this.router.navigate(['/affaires']);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.errorMessage.set(err?.error?.message || err?.error || 'Une erreur est survenue lors de la mise à jour de l\'affaire');
      }
    });
  }
}
